import { t } from './dictionary.js';

/**
 * Returns the page numbers to display, inserting '...' for gaps.
 * @param {number} currentPage
 * @param {number} totalPages
 * @returns {Array<number|string>}
 */
function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, '...', totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
}

/**
 * Creates the previous button.
 * @param {number} currentPage
 * @param {Function} onPageChange
 * @returns {HTMLButtonElement}
 */
function createPrevBtn(currentPage, onPageChange) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'hq-pagination__button';
  if (currentPage === 1) btn.disabled = true;
  btn.textContent = t('pagination.previous');
  btn.addEventListener('click', () => onPageChange(currentPage - 1));
  return btn;
}

/**
 * Creates the next button.
 * @param {number} currentPage
 * @param {number} totalPages
 * @param {Function} onPageChange
 * @returns {HTMLButtonElement}
 */
function createNextBtn(currentPage, totalPages, onPageChange) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'hq-pagination__button';
  if (currentPage === totalPages) btn.disabled = true;
  btn.textContent = t('pagination.next');
  btn.addEventListener('click', () => onPageChange(currentPage + 1));
  return btn;
}

/**
 * Creates all page number <li> elements.
 * @param {number} currentPage
 * @param {number} totalPages
 * @param {Function} onPageChange
 * @returns {HTMLLIElement[]}
 */
function createPageItems(currentPage, totalPages, onPageChange) {
  return getPageNumbers(currentPage, totalPages).map((page) => {
    const li = document.createElement('li');
    li.className = 'hq-pagination__item';

    if (page === '...') {
      const span = document.createElement('span');
      span.className = 'hq-pagination__ellipsis';
      span.textContent = '\u2026';
      span.setAttribute('aria-hidden', 'true');
      li.appendChild(span);
    } else if (page === currentPage) {
      li.className = 'hq-pagination__item';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hq-pagination__button';
      btn.textContent = page;
      btn.setAttribute('aria-current', 'page');
      li.appendChild(btn);
    } else {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hq-pagination__button';
      btn.textContent = page;
      btn.setAttribute('aria-label', t('pagination.page')(page));
      btn.addEventListener('click', () => onPageChange(page));
      li.appendChild(btn);
    }

    return li;
  });
}

/**
 * Renders pagination controls into the nav element.
 *
 * DOM : btn prev (desktop) → <ol> pages → btn prev (mobile) → btn next
 *
 * The prev button is duplicated so each breakpoint has its own instance in
 * natural DOM order — no CSS `order` is needed.
 * CSS display:none hides the inactive prev button per breakpoint.
 *
 * @param {Object} options
 * @param {number}      options.currentPage
 * @param {number}      options.totalPages
 * @param {HTMLElement} options.el           - The <nav> element to render into.
 * @param {Function}    options.onPageChange - Callback called with the new page number.
 */
export function renderPagination({ currentPage, el, onPageChange, totalPages }) {
  el.innerHTML = '';
  el.className = 'hq-pagination';
  el.setAttribute('aria-label', t('pagination.ariaLabel'));

  if (totalPages <= 1) return;

  const ol = document.createElement('ol');
  ol.className = 'hq-pagination__list';
  createPageItems(currentPage, totalPages, onPageChange).forEach((li) => ol.appendChild(li));

  const prevDesktop = createPrevBtn(currentPage, onPageChange);
  prevDesktop.classList.add('hq-pagination__button--desktop');

  const prevMobile = createPrevBtn(currentPage, onPageChange);
  prevMobile.classList.add('hq-pagination__button--mobile');

  el.appendChild(prevDesktop);
  el.appendChild(ol);
  el.appendChild(prevMobile);
  el.appendChild(createNextBtn(currentPage, totalPages, onPageChange));
}
